import React, { useState, useEffect } from "react";
import { IoIosStar } from "react-icons/io";
import { BsFillPencilFill } from "react-icons/bs";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { MdOutlineRateReview } from "react-icons/md"; // Import an icon for the empty state

// --------------------------------------------------
// 🔹 1. ReviewCard Component (With animations and enhanced styling)
// --------------------------------------------------
const ReviewCard = ({ author, avatarUrl, rating, text, relativeTime }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRendered, setIsRendered] = useState(false);

  useEffect(() => {
    // Trigger the animation shortly after the component mounts
    const timer = setTimeout(() => setIsRendered(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const canTruncate = text.length > 200;
  const displayText = isExpanded
    ? text
    : `${text.slice(0, 200)}${canTruncate ? "..." : ""}`;

  return (
    <div
      className={`
        bg-white rounded-xl p-6 border border-gray-200 shadow-sm
        transition-all duration-500 ease-out
        hover:shadow-xl hover:scale-[1.011]
        ${isRendered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}
      `}
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6">
        <div className="flex items-center gap-3 sm:gap-4">
          <img
            src={avatarUrl}
            alt={author}
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-gray-200 flex-shrink-0"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/img/avatar.png";
            }}
          />
          <div>
            <p className="font-bold text-gray-900 text-lg sm:text-xl">
              {author}
            </p>
            <p className="text-sm text-gray-500 font-medium">{relativeTime}</p>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <IoIosStar
              key={star}
              className={`w-5 h-5 sm:w-6 sm:h-6 ${
                star <= rating ? "text-orange-500" : "text-gray-300"
              }`}
            />
          ))}
        </div>
      </div>
      <p className="text-gray-700 mt-4 text-base leading-relaxed italic">
        "{displayText}"
      </p>
      {canTruncate && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-orange-600 font-bold mt-3 text-sm hover:underline"
        >
          {isExpanded ? "Read Less" : "Read More"}
        </button>
      )}
    </div>
  );
};

const GoogleReviewItem = ({ review }) => (
  <ReviewCard
    author={review.author_name || "Anonymous"}
    avatarUrl={review.profile_photo_url || "/img/avatar.png"}
    rating={review.rating || 0}
    text={review.text || "No comment provided."}
    relativeTime={review.relative_time_description || "Recently"}
  />
);

const FlippedReviewItem = ({ review }) => (
  <ReviewCard
    author={review.user || "Anonymous"}
    avatarUrl={review.profile_photo_url || "/img/avatar.png"}
    rating={review.rating || 0}
    text={review.comment || "No comment provided."}
    relativeTime={
      review.date ? new Date(review.date).toLocaleDateString() : "Recently"
    }
  />
);

const ReviewsSection = ({ vendor, onReviewSuccess, onWriteReviewClick }) => {
  
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (vendor && (vendor.vendor_id || vendor.google_rating !== undefined)) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [vendor]);

  
  if (!vendor) return null;
  const hasGoogleData =
    vendor?.google_review_count > 0 || vendor?.google_rating > 0;
  const placeId = vendor?.placeId;

  const reviewsData = hasGoogleData
    ? vendor.google_reviews_data || []
    : vendor.reviews_data || [];
  const overallRating = hasGoogleData
    ? vendor.google_rating
    : Number(vendor.start_rating) || 0;
  const totalReviews = hasGoogleData
    ? vendor.google_review_count
    : Number(vendor.total_review) || 0;
  const reviewSource = hasGoogleData ? "Google" : "Flipped";

  const ReviewItem = hasGoogleData ? GoogleReviewItem : FlippedReviewItem;

 // const [showAllReviews, setShowAllReviews] = useState(false);
 // const [loading, setLoading] = useState(true);

 // useEffect(() => {
   // if (vendor && (vendor.vendor_id || vendor.google_rating !== undefined)) {
     // setLoading(false);
   // } else {
    //  setLoading(true);
   // }
 // }, [vendor]);

  const displayedReviews = showAllReviews
    ? reviewsData
    : reviewsData.slice(0, 3);
  const hasMore = reviewsData.length > 3;

  if (loading) {
    return (
      <section className="bg-gradient-to-b from-gray-50 to-gray-100 p-6 sm:p-8 rounded-xl">
        <div className="text-center py-12 text-gray-500">
          Loading reviews...
        </div>
      </section>
    );
  }
  return (
    <section className={`bg-gradient-to-b from-gray-50 to-gray-100 rounded-2xl ${
        reviewsData.length === 0 ? "p-4" : "p-4 sm:p-6 md:p-8"
      }`}>
      <div className={`flex flex-col md:flex-row justify-between items-start gap-6 ${
        reviewsData.length === 0 ? "mb-4" : "mb-10"
      }`}>
        <div className="flex-1">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#000048] mb-2">
            What People Are Saying
          </h2>

          {overallRating > 0 && (
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 bg-white border border-gray-200 px-3 sm:px-4 py-2 rounded-full w-fit shadow-sm">
              <span className="font-bold text-base sm:text-lg lg:text-xl text-gray-800">
                {overallRating.toFixed(1)}
              </span>

              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <IoIosStar
                    key={i}
                    className={`w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 ${
                      i < Math.round(overallRating)
                        ? "text-orange-500"
                        : "text-gray-300"
                    }`}
                  />
                ))}
              </div>

              <span className="text-xs sm:text-sm lg:text-base text-gray-600 font-medium">
                from {totalReviews.toLocaleString()} reviews on {reviewSource}
              </span>
            </div>
          )}
        </div>

        <div className="w-full md:w-auto self-stretch md:self-center">
          {hasGoogleData ? (
            placeId &&
            placeId.length > 0 && (
              <button
                onClick={() =>
                  window.open(
                    `https://search.google.com/local/writereview?placeid=${placeId}`,
                    "_blank"
                  )
                }
                className="w-full h-full justify-center inline-flex items-center gap-3 px-6 py-3 rounded-xl text-base font-bold bg-[#FF6216] text-white transform hover:scale-105 transition-transform duration-200 shadow-lg hover:shadow-xl"
              >
                <BsFillPencilFill /> Write a Google Review
              </button>
            )
          ) : (
            <button
              onClick={onWriteReviewClick}
              className="w-full h-full justify-center inline-flex items-center gap-3 px-6 py-3 rounded-xl text-base font-bold bg-[#FF6216] text-white transform hover:scale-105 transition-transform duration-200 shadow-lg hover:shadow-xl"
            >
              <BsFillPencilFill /> Share Your Story
            </button>
          )}
        </div>
      </div>

      {reviewsData.length === 0 ? (
        <div className="text-center py-3 text-gray-500 bg-white rounded-xl border-2 border-dashed">
          <MdOutlineRateReview className="mx-auto text-gray-400 w-10 h-10 mb-1" />
          <h3 className="text-lg font-semibold text-gray-800">
            No reviews yet.
          </h3>
          <p className="mt-1 text-base">
            Be the first to share your experience.
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-6">
            {displayedReviews.map((review) => (
              <ReviewItem
                key={review._id || review.time || review.author_name}
                review={review}
              />
            ))}
          </div>
          {hasMore && (
            <div className="text-center mt-8 md:mt-12">
              <button
                onClick={() => setShowAllReviews(!showAllReviews)}
                className="inline-flex items-center gap-2 md:gap-3 px-6 py-3 md:px-8 md:py-4 text-base md:text-lg bg-white text-[#FF6216] border-2 border-[#FF6216] rounded-full font-bold hover:bg-[#FFF5EE] transition-all shadow-md hover:shadow-lg transform hover:scale-105"
              >
                {showAllReviews ? (
                  <>
                    Show Less <FaChevronUp className="w-4 h-4 md:w-5 md:h-5" />
                  </>
                ) : (
                  <>
                    Load All {reviewsData.length} Reviews{" "}
                    <FaChevronDown className="w-4 h-4 md:w-5 md:h-5" />
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default ReviewsSection;
